import { Checkbox, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useState } from "react";

import { cpfMask } from "utils/cpf";
import api from "services/api";

import { Background, Card, Wrapper, Title, Subtitle, Paragraph, Link, Button } from "./styled";
import { MissingDataMultistep } from "./MissingDataMultistep";
import { IResponse } from "./MissingDataForm.types";
import { IMissingData } from "./types";

function mapDataToMissingData(data: IResponse): IMissingData {
  const employee = data.employee;
  
  return {
    tax_payer_id: employee.TAX_PAYER_ID || "",
    name: employee.NAME || "",
    marital_status_id: employee.MARITAL_STATUS_ID || "",
    zip_code: employee.ZIP_CODE || "",
    address_type: employee.ADDRESS_TYPE || "",
    address: employee.ADDRESS || "",
    address_number: employee.ADDRESS_NUMBER || "",
    complement: employee.COMPLEMENT || "",
    neighborhood: employee.NEIGHBORHOOD || "",
    state_id: employee.STATE_ID || "",
    city_id: employee.CITY_ID || "",
    city_number: "",
    birth_date: employee.BIRTH_DATE ? String(employee.BIRTH_DATE) : null,
    hiring_date: employee.HIRING_DATE ? String(employee.HIRING_DATE) : null,
    registration_number: employee.NATIONAL_IDENTITY_NUMBER || "",
    gender_id: employee.GENDER_ID || "",
    mother_name: employee.MOTHER_NAME || "",
    plan_name: employee.PLAN || "",
    cnpj: employee.LEGAL_ENTITY_TAX_ID || "",
    phoneNumber: employee.PHONE_NUMBER || undefined,
    race: employee.RACE_ID || undefined,
    educationLevel: employee.EDUCATION_LEVEL_ID || undefined,
    nationalityCountry: employee.NATIONALITY_COUNTRY_ID || undefined,
    birthCountry: employee.BIRTH_COUNTRY_ID || undefined,
    birthState: employee.BIRTH_STATE_ID || undefined,
    weddingDate: employee.WEDDING_DATE ? String(employee.WEDDING_DATE) : undefined,
    nationalIdentityNumber: employee.NATIONAL_IDENTITY_NUMBER || undefined,
    nationalIdentityEmissor: employee.NATIONAL_IDENTITY_EMISSOR || undefined,
    nationalIdentityExpeditionDate: employee.NATIONAL_IDENTITY_EXPEDITION_DATE || undefined,
    jobTitle: employee.JOB_TITLE || undefined,
    paymentFrequency: employee.PAYMENT_FREQUENCY || undefined,
    grossPayment: employee.GROSS_PAYMENT || undefined,
    cbo: employee.CBO || undefined,
    isPj: employee.ORIGIN === "PJ",
    dependents: employee.dependents.map(dep => ({
      tax_payer_id: dep.TAX_PAYER_ID || "",
      dependent_type_id: dep.DEPENDENT_TYPE_ID || "",
      name: dep.NAME || "",
      marital_status_id: dep.MARITAL_STATUS_ID || "",
      birth_date: dep.BIRTH_DATE ? String(dep.BIRTH_DATE) : null,
      mother_name: dep.MOTHER_NAME || "",
      plan_option: dep.PLAN || "",
      // Map IS_STUDENT boolean to "SIM"/"NAO" string format expected by form
      student: dep.IS_STUDENT === true ? "SIM" : dep.IS_STUDENT === false ? "NAO" : "",
    }))
  };
}

export function MissingDataForm() {
  const { workspaceActivityId, campaignId } = useParams() as { workspaceActivityId: string, campaignId: string };

  const [data, setData] = useState<IResponse | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [cpf, setCpf] = useState('');

  const { isPending, isError, error, mutate } = useMutation({
    mutationFn: async () => {
      try {
        const { data } = await api.post<{ data: IResponse }>(`/v2/forms/missing-data/${workspaceActivityId}/${campaignId}`, { taxPayerId: cpf});

        return data.data;
      } catch(err) {
        throw new Error("CPF inválido.");
      }
    },
    onSuccess: (data) => {
      setData(data);
      console.log("[Success @ MissingDataForm]", data);
    },
    onError: (err) => {
      console.error("[Error @ MissingDataForm]", err);
    },
  });

  return (
    <Background>
      <Card>
        {!data && (
          <Wrapper>
            <div style={{ width: "100%", maxWidth: "530px", display: "flex", flexDirection: "column", gap: "8px" }}>
              <Title>Confirmação de Identidade</Title>
              <Subtitle>Seja bem-vindo(a)!</Subtitle>
              <Paragraph>
                Para garantir que você e seus dependentes tenham seu plano de
                saúde e/ou odontológico devidamente atualizado, solicitamos que
                preencha os campos em branco e confirme se as informações estão
                corretas. Esse é um passo essencial para ativar seu benefício
                corretamente, evitando problemas de atendimento futuro.
              </Paragraph>
              <Paragraph style={{ marginBottom: "40px" }}>
                Garantimos que seus dados serão mantidos em sigilo, sendo
                utilizados exclusivamente para atualização cadastral junto à
                seguradora.
              </Paragraph>
              <TextField
                label="CPF"
                variant="outlined"
                size="medium"
                type="text"
                value={cpfMask(cpf)}
                placeholder="Digite seu CPF..."
                onChange={(e) => setCpf(e.target.value.replace(/\D/g, '').substring(0, 11))}
                disabled={isPending}
                error={isError}
                helperText={isError && (error?.message || "Algo deu errado.")}
              />
              <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", margin: "20px 0px" }}>
                <Checkbox 
                  size="small"
                  value={isChecked}
                  disabled={isPending}
                  onChange={() => setIsChecked(prev => !prev)} 
                />
                <Link>Li e estou de acordo com a <a href='https://www.conducco.ai/privacy-policy' target="_blank">Política de Privacidade</a> da plataforma.</Link>
              </div>
              <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
                <Button 
                  variant="contained"
                  disabled={!cpf || !isChecked || isPending}
                  onClick={() => mutate()}
                >
                  {isPending ? 'Carregando...' : 'Próximo'}
                </Button>
              </div>
            </div>
          </Wrapper>
        )}
        {/* {data && <div>Dados</div>} */}
        {data && <MissingDataMultistep missingData={mapDataToMissingData(data)} config={data.config} />}
      </Card>
    </Background>
  );
}
